import { Link } from '@tanstack/react-router';
import { postMeta } from './manifest';
import type { Post } from './types';
import { H2, H3, OL, P, Pull, UL } from './ui';

const post: Post = { meta: postMeta('beat-battle-vs-rap-battle'), Body };
export default post;

function Body() {
  return (
    <>
      <P>
        People mix these up constantly. They share a word and a vibe, but a beat battle and a rap
        battle are different sports with different rules, different judges, and different winners.
        If you are searching for one and ending up at the other, this is the page that sorts it out.
      </P>

      <Pull>A beat battle is a producer competition. A rap battle is an MC competition.</Pull>

      <P>
        That is the whole answer in one sentence. Below is the longer one, with the actual mechanics
        of each.
      </P>

      <H2 id="quick-comparison">Quick comparison</H2>
      <UL>
        <li>
          <strong>Beat battle</strong>: producers compete by making instrumentals. No vocals, no
          words. Judging is on production quality (drums, bass, melody, mix).
        </li>
        <li>
          <strong>Rap battle</strong>: MCs compete with bars. The instrumental, if any, is just a
          backdrop. Judging is on lyricism, flow, delivery, punchlines, and crowd reaction.
        </li>
        <li>
          <strong>Format</strong>: beat battles often run online, with timed rounds and a shared
          sample kit. Rap battles are typically live, face-to-face, with an MC trading verses.
        </li>
        <li>
          <strong>Length</strong>: a beat is usually 60 to 120 seconds. A rap battle round is 30 to
          90 seconds of bars per MC.
        </li>
      </UL>

      <H2 id="what-is-a-beat-battle">What is a beat battle?</H2>
      <P>
        A beat battle is a head-to-head or many-way competition between music producers. Each
        producer makes an original instrumental, usually under a tight deadline, and the entries are
        scored against each other. Modern online platforms like{' '}
        <Link to="/" className="underline decoration-primary/50 underline-offset-2">
          Prod Battle
        </Link>{' '}
        run them in real time: everyone gets the same sample kit at the same moment, a timer starts,
        and at the end the audience votes blind on the submissions.
      </P>
      <H3>The core elements</H3>
      <OL>
        <li>
          <strong>A sample kit or constraint.</strong> Many battles drop the same drums, melodies,
          and one-shots on every producer so the contest is about creative use, not gear.
        </li>
        <li>
          <strong>A timer.</strong> Anywhere from 5 minutes (speed battles) to a full week (async).
          Real-time platforms run 10 to 30 minutes per round.
        </li>
        <li>
          <strong>Submissions.</strong> Each producer uploads a beat, usually 60 to 120 seconds of
          music with a clear arrangement.
        </li>
        <li>
          <strong>Voting.</strong> Audience or judge panel scores entries. Blind voting (no producer
          names visible) keeps it about the music.
        </li>
      </OL>

      <H2 id="what-is-a-rap-battle">What is a rap battle?</H2>
      <P>
        A rap battle is a verbal duel between two or more MCs. Each takes a turn dropping bars
        directly aimed at the opponent: clever rhymes, punchlines, wordplay, character attacks
        (mostly performative). Some battles are a cappella; some run over an instrumental, but the
        beat is incidental. The win comes from rhetorical skill, not production.
      </P>
      <H3>The core elements</H3>
      <OL>
        <li>
          <strong>Rounds.</strong> Each MC gets a fixed time, typically one to three minutes, in
          alternating rounds.
        </li>
        <li>
          <strong>Bars.</strong> Lines have to land. Wordplay, doubles, multi-syllabic rhymes,
          internal rhymes, and references all score.
        </li>
        <li>
          <strong>Performance.</strong> Voice control, timing, body language, and the ability to
          draw a reaction from the crowd matter as much as the words.
        </li>
        <li>
          <strong>Judging.</strong> Often a panel of judges or a live crowd. Some leagues use
          structured scoring; many just pick a winner per round.
        </li>
      </OL>

      <H2 id="who-judges">Who judges what</H2>
      <P>In a beat battle the audience is listening for:</P>
      <UL>
        <li>Drum programming and groove</li>
        <li>Bassline movement and weight (especially the 808 in trap)</li>
        <li>Melody choice and chord progression</li>
        <li>Arrangement (intro, verse, drop, breakdown)</li>
        <li>Mix loudness and balance</li>
        <li>Creative use of the shared sample kit</li>
      </UL>
      <P>In a rap battle the crowd is listening for:</P>
      <UL>
        <li>Punchline density</li>
        <li>Rhyme scheme complexity</li>
        <li>How well the bars are aimed at the opponent</li>
        <li>Delivery, breath control, cadence</li>
        <li>Crowd-moving moments ("haymaker bars")</li>
        <li>Originality - did they freestyle or write?</li>
      </UL>

      <H2 id="formats">Common formats</H2>
      <H3>Beat battle formats</H3>
      <UL>
        <li>
          <strong>Live, real-time, shared kit.</strong> Same sample pack drops to every producer.
          Timer starts. Submissions go up. Audience votes. This is the format Prod Battle uses.
        </li>
        <li>
          <strong>Async with theme.</strong> A theme (genre, BPM, sample) is announced. Producers
          have a week to submit. Voting happens at the end.
        </li>
        <li>
          <strong>Tournament bracket.</strong> Many producers compete over multiple rounds. Win your
          match, advance. Losers are eliminated. Final round picks a champion.
        </li>
        <li>
          <strong>Speed battle.</strong> 5 or 10 minutes total. Tests workflow, not depth.
        </li>
      </UL>
      <H3>Rap battle formats</H3>
      <UL>
        <li>
          <strong>A cappella.</strong> No instrumental. Most modern leagues (URL, KOTD) use this.
          Pure bars.
        </li>
        <li>
          <strong>Over a beat.</strong> Older/freestyle formats. The MC raps over a generic
          instrumental.
        </li>
        <li>
          <strong>Cypher.</strong> Many MCs in a circle, each taking a turn. Less head-to-head, more
          showcase.
        </li>
        <li>
          <strong>Written vs freestyle.</strong> Freestyle is improvised in the moment. Written is
          prepared. Modern battle rap is mostly written, with freestyle reserved for cyphers.
        </li>
      </UL>

      <H2 id="why-mixed-up">Why people mix these up</H2>
      <P>
        Both come out of hip-hop culture and both involve the word "battle". Many rap battle leagues
        started under the broader "beat battle" umbrella in the 1990s before rap battling became its
        own scene. Today the lines are clear: if there are bars, it is a rap battle; if there is
        just an instrumental, it is a beat battle. A beatmaker can rap, and an MC can produce, but
        the contests judge different things.
      </P>

      <H2 id="can-you-do-both">Can you do both?</H2>
      <P>
        Sure. Plenty of producer-MCs run their own beats. Some leagues have hybrid events where the
        same person submits a beat and then performs over it. But in any single contest, you are
        being judged for one or the other. Pick the discipline you are stronger in for the actual
        scoring round.
      </P>

      <H2 id="where-to-compete">Where to compete in each</H2>
      <P>
        For beat battles, the easiest entry is online. Sign up free at{' '}
        <Link to="/" className="underline decoration-primary/50 underline-offset-2">
          Prod Battle
        </Link>
        , pick a genre, and you are in your first match within a minute. We run quickplay battles
        all day across trap, house, drum and bass, hip-hop, lo-fi, phonk, and more. Weekly
        tournaments stack the best producers per genre against each other for a ranked title.
      </P>
      <P>
        For rap battles, the major leagues are URL (Ultimate Rap League), KOTD (King of the Dot),
        and PremierBattles in the UK. Most cities also have local cyphers - the easiest way in is to
        find an open-mic event and start there.
      </P>

      <H2 id="bottom-line">Bottom line</H2>
      <P>
        If you produce music, you want a beat battle. If you write rhymes and perform them, you want
        a rap battle. They are both descended from the same culture, but the scoring, formats, and
        skills are different sports.
      </P>
      <P>
        Want to try a beat battle right now?{' '}
        <Link to="/play" className="underline decoration-primary/50 underline-offset-2">
          Pick a mode and start
        </Link>
        .
      </P>
    </>
  );
}
